
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import { saveAs } from 'file-saver';
import { Action, State } from 'vuex-class';
import { CitationsImportState } from '@/store/types/general.types';
import { RootState } from '@/store/store';
import { ImportCitationRequestPayload } from '@/store/modules/documents/types/citations.types';
import RadialProgressBar from '@/jbi-shared/vue-components/RadialProgressBar.vue';
import Link from '@/components/base/Link.vue';
import UploadError from './UploadError.vue';
import UploadComplete from './UploadComplete.vue';
import UploadLoading from './UploadLoading.vue';
import UploadTimeout from './UploadTimeout.vue';
import { useRouteParams } from '@/utils/route.util';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { ToastProgrammatic as Toast, Upload } from 'buefy';
import { Job } from 'bull';
import { get as _get } from 'lodash';
import { ENV } from '@/utils/env.utils';
import { VueRef } from '@/utils/vue.types';
import { SwitchObject } from '@/jbi-shared/types/helpers';

@Component({
  components: {
    Link,
    RadialProgressBar,
    UploadError,
    UploadComplete,
    UploadLoading,
    UploadTimeout
  }
})
export default class CitationUploader extends Vue {
  public dropFiles: File | null = null;
  @Prop() public revisionId!: number;

  @State((state: RootState) => state.projects.apiState.importCitations)
  public citationsImportState!: CitationsImportState;

  @Action('projects/importCitations') importCitations!: (
    payload: ImportCitationRequestPayload
  ) => Promise<Job>;

  @Action('projects/resetCitationImport') resetCitationImport!: () => Promise<
    void
  >;

  get isCitationsImportOngoing() {
    return (
      this.citationsImportState.uploading ||
      this.citationsImportState.processing
    );
  }

  get isCitationImportTimedOut() {
    return this.citationsImportState.uploadTimeout;
  }

  get importSummary() {
    const workerInfo = this.citationsImportState.workerInfo;

    if (
      !workerInfo ||
      !workerInfo.returnvalue ||
      !workerInfo.returnvalue.status
    )
      return {};

    const metaData = workerInfo.returnvalue.metadata
      ? workerInfo.returnvalue.metadata
      : {};

    const jobResult =
      this.citationsImportState.success || this.citationsImportState.partial
        ? JSON.parse(workerInfo.returnvalue.message)
        : {};

    return {
      ...jobResult,
      ...metaData
    };
  }

  get documentId() {
    return +useRouteParams.call(this).documentId;
  }

  get projectId() {
    return +useRouteParams.call(this).projectId;
  }

  // browsers usually tend to open text files in a new tab
  // this is to force a file download
  private async downloadSampleFile(sampleType: string) {
    const ref = this.$refs.sampleDownloadLink as VueRef<HTMLAnchorElement>;
    const element = ref.$el;

    if (element.classList.contains('progress')) return;

    element.classList.add('progress');

    try {
      // IIF equivalent to a switch statement with a default case to show an alert
      ((
        ({
          ['endnote']: () =>
            saveAs(
              'https://storage.googleapis.com/cplus-static-documents/sample_endnote.xml',
              'endnote.xml'
            )
        } as SwitchObject<string, () => void>)[sampleType] ||
        (() => alert(`Unknown sample type: ${sampleType}`))
      )());
      // end IIF
    } catch (error) {
      alert('could not download sample file');
    }

    element.classList.remove('progress');
  }

  @Watch('dropFiles')
  @isTruthy
  public async handleFileUpload(file: File) {
    const MAX_SIZE_MB = ENV.CPLUS2_CITATIONS_MAX_FILE_SIZE_MB;
    const fileName = (file as File).name;

    const toast = (message: string, type = 'is-danger') =>
      Toast.open({
        message: message,
        duration: 3000,
        type: type
      });

    const validationError = [
      {
        validate: () => /[~`!#$%\^&*+=\\[\]\\';,/{}|\\"<>\?]/g.test(fileName),
        message: `The file "${fileName}" contains special characters`
      },
      {
        validate: () => file.size > MAX_SIZE_MB * 1024 * 1024,
        message: `The file "${fileName}" is too large. (${MAX_SIZE_MB}MB max)`
      }
    ].find((validation) =>
      validation.validate() ? validation.message : false
    );

    if (validationError) {
      toast(validationError.message);
      this.clearFileUploader();
      return;
    }

    const result = await this.importCitations({
      projectId: this.projectId,
      revisionId: this.revisionId,
      file
    });

    if (!result) return;

    const metadata = result.returnvalue.metadata;

    if (metadata.total > 0 && metadata.total === metadata.success) {
      toast(`Successfully imported citation(s) from ${fileName}`, 'is-success');
      this.resetState();
    }
  }

  async resetState() {
    this.clearFileUploader();
    await this.resetCitationImport();
  }

  clearFileUploader() {
    const uploadRef = this.$refs.fileInput as Upload;
    const fileInput = uploadRef?.getElement();

    if (fileInput) fileInput.value = '';

    this.dropFiles = null;
  }

  public destroyed() {
    if (this.isCitationsImportOngoing) {
      Toast.open({
        queue: true,
        message: `Citation import is running in the background`
      });
    }
  }
}
